import { LoadingView } from "@/features/Auth/components/LoadingView";
import { Illustrations } from "@/illustrations";
import {
  Avatar,
  Container,
  Divider,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
} from "@chakra-ui/react";
import { EmptyState, Icon, MenuItem } from "@pufflig/prompt-studio-ui";
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { format } from "date-fns/format";

interface PromptbookRowData {
  id: string;
  name: string;
  owner: {
    name: string;
    picture: string;
  };
  modified: string;
}

interface TableProps {
  data: PromptbookRowData[];
  isLoadingData: boolean;
  isReadOnly?: boolean;
  onSaveAsTemplate?: (recipeId: string) => void;
  onSelectRow: (rowId: string) => void;
  onDeletePromptbook: (recipeId: string) => void;
  onRenamePromptbook: (recipeId: string) => void;
  onDuplicatePromptbook: (recipeId: string) => void;
}

const columnHelper = createColumnHelper<PromptbookRowData>();

export const PromptbookTable = (props: TableProps) => {
  const {
    data,
    isLoadingData,
    isReadOnly,
    onSelectRow,
    onDeletePromptbook,
    onRenamePromptbook,
    onDuplicatePromptbook,
    onSaveAsTemplate,
  } = props;

  const isLargeScreen = useBreakpointValue({
    base: false,
    lg: true,
  });

  const columns = [
    columnHelper.accessor("name", {
      header: "Name",
      cell: (info) => (
        <Flex>
          <Text textStyle="label/M/regular" overflow="hidden" textOverflow="ellipsis">
            {info.getValue()}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor("owner", {
      header: isLargeScreen ? "Created By" : "Owner",
      cell: (info) => (
        <Stack direction="row" alignItems="center">
          <Avatar size="sm" name={info.getValue().name} src={info.getValue().picture} />
          <Text textOverflow="ellipsis" display={isLargeScreen ? "inline-block" : "none"}>
            {info.getValue().name}
          </Text>
        </Stack>
      ),
    }),
    columnHelper.accessor("modified", {
      header: "Modified",
      cell: (info) => (
        <div>
          <Text whiteSpace="normal" overflowWrap="break-word">
            {format(info.getValue(), "PP")}
          </Text>
        </div>
      ),
    }),
    columnHelper.accessor("id", {
      id: "action",
      header: "",
      size: 36,
      cell: (info) => (
        <Flex justifyContent="flex-end">
          <Menu>
            <MenuButton
              isDisabled={isReadOnly}
              as={IconButton}
              onClick={(e) => {
                e.stopPropagation();
              }}
              variant="ghost"
              size="sm"
              aria-label="options"
              icon={<Icon name="more" />}
              _hover={{ bg: "background.tertiary" }}
            >
              Actions
            </MenuButton>
            <MenuList>
              {onSaveAsTemplate && (
                <>
                  <MenuItem
                    leftIcon={<Icon name="export-3" />}
                    primaryLabel="Save as Template"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSaveAsTemplate?.(info.getValue() as string);
                    }}
                  />
                  <Divider />
                </>
              )}
              <MenuItem
                leftIcon={<Icon name="edit" />}
                primaryLabel="Rename"
                onClick={(e) => {
                  e.stopPropagation();
                  onRenamePromptbook(info.getValue() as string);
                }}
              />
              <MenuItem
                leftIcon={<Icon name="copy" size="md" />}
                primaryLabel="Duplicate"
                onClick={(e) => {
                  e.stopPropagation();
                  onDuplicatePromptbook(info.getValue() as string);
                }}
              ></MenuItem>
              <MenuItem
                leftIcon={<Icon name="trash" />}
                primaryLabel="Delete"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeletePromptbook(info.getValue() as string);
                }}
              />
            </MenuList>
          </Menu>
        </Flex>
      ),
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const isTableEmpty = !data.length;

  return (
    <>
      {isLoadingData && (
        <Container height="10rem">
          <LoadingView />
        </Container>
      )}
      {!isLoadingData && (
        <>
          {isTableEmpty ? (
            <EmptyState
              showBackground
              title="No Promptbooks yet"
              description="Create new Promptbook to start. Your Promptbooks will appear here."
              Illustration={<Illustrations.StartupEmptyPromptbook />}
            />
          ) : (
            <Flex direction="column" height="100%">
              <TableContainer flex="1" overflowX="auto">
                <Table>
                  <Thead height="3.375rem">
                    {table.getHeaderGroups().map((headerGroup) => (
                      <Tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <Th
                            overflow="hidden"
                            key={header.id}
                            padding="0"
                            px="1.5rem"
                            borderBottom="1px solid"
                            borderColor="border.secondary"
                            maxWidth={header.column.getSize()}
                            width={
                              header.id == "owner" && !isLargeScreen ? "6.5em" : header.id == "action" ? "2em" : "9em"
                            }
                          >
                            <Text textStyle="label/S/regular" color="foreground.secondary" textTransform="none">
                              {header.isPlaceholder
                                ? null
                                : flexRender(header.column.columnDef.header, header.getContext())}
                            </Text>
                          </Th>
                        ))}
                      </Tr>
                    ))}
                  </Thead>
                  <Tbody>
                    {table.getRowModel().rows.map((row) => (
                      <Tr
                        tabIndex={0}
                        _hover={{
                          bg: "background.secondary",
                        }}
                        height="3.375rem"
                        cursor={"pointer"}
                        onClick={() => onSelectRow(row.original.id)}
                        key={row.id}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <Td
                            overflow="hidden"
                            border="none"
                            borderBottom="1px solid"
                            borderColor="border.secondary"
                            padding="0"
                            px="1.5rem"
                            flex="1"
                            key={cell.id}
                            maxWidth={cell.column.getSize()}
                            width={
                              cell.column.id == "owner" && !isLargeScreen
                                ? "6.5em"
                                : cell.column.id == "action"
                                  ? "2em"
                                  : "9em"
                            }
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </Td>
                        ))}
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </Flex>
          )}
        </>
      )}
    </>
  );
};
